/**
 * Active profile — the competition's profile_id.
 *
 * The profile is a property OF the competition (competition.key.profile_id), so
 * the competition document is the source of truth.
 *
 * NOT STORED. This used to cache the id in localStorage, which is shared by
 * every tab on the origin: two tabs on two competitions had one key between
 * them, so whichever changed competition last decided the profile for both. The
 * id now travels as a prop from the page that owns the competition selection,
 * which is per-tab by construction and — because it is derived from the
 * selected competition rather than written alongside it — cannot drift out of
 * step with the competition the way a separate write could.
 *
 * Anything reading a profile for an event write should prefer the
 * fixture-derived value, which is what resolveProfileId does.
 */

/**
 * Every competition that predates profiles resolves here. Matches the
 * importers' own default, so a competition with no profile_id is treated the
 * same on both sides rather than the UI and the backend disagreeing about what
 * it is.
 */
export const DEFAULT_PROFILE_ID = 'football_pls';

/**
 * The profile every component should use, resolved in priority order:
 *
 *   1. the loaded fixture's competition — authoritative
 *   2. the selected competition's profile — covers the window before fixture
 *      data arrives, passed down from the page that owns the selection
 *   3. DEFAULT_PROFILE_ID — nothing selected and no fixture yet
 *
 * Tier 2 is not optional decoration. Callers once read (1) alone with no
 * fallback, so `profileId` was null until the fixture snapshot arrived. That
 * null reached event payloads, and the event service throws on it ("profile_id
 * is required to resolve plugin path"). It also made getClockProfile fall to the
 * `default` clock profile mid-load, which enables clock-derived period
 * transitions that football_pls deliberately disables via sentinel limits.
 *
 * @param {string} [fixtureProfileId] fixtureState.competition[0].profile_id
 * @param {string} [selectedProfileId] profile of the currently selected competition
 */
export function resolveProfileId(fixtureProfileId, selectedProfileId) {
  return fixtureProfileId || selectedProfileId || DEFAULT_PROFILE_ID;
}
